import React, { useState, useEffect, useMemo } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import clsx from "clsx";
// Components
import { FaEdit, FaCheck } from "react-icons/fa";
import Switch from "@/components/common/switch/switch";

// Services
import { syncUser, findUserNotificationSetting } from "@/services/UserService";

// Constants
import { NOTIFICATION_TYPES, DEFAULT_FORM_DATA } from "@/components/constants/notification-settings.constants";

import useLogout from "@/hooks/useLogout";

interface User {
    id: string;
    emailAddress: string;
    nickname: string;
}

interface NotificationSetting {
    id: string;
    message_type: string;
    email_frequency: string;
    push_frequency: string;
}

interface FormData {
    emailAddress: string;
    nickname: string;
    notifications: {
        statusChanges: boolean;
        commentsOnMyListings: boolean;
        newListings: boolean;
    };
}

interface Errors {
    nickname: string;
}

interface UserSettingsProps {
    user: User;
}

/**
 * UserSettings component for managing user profile and notification preferences
 * @param {Object} props - Component props
 * @param {Object} props.user - User object containing profile information
 * @returns {JSX.Element} UserSettings component
 */
const UserSettings: React.FC<UserSettingsProps> = ({ user }) => {
    const [formData, setFormData] = useState<FormData>({
        ...DEFAULT_FORM_DATA,
        emailAddress: user.emailAddress || "",
        nickname: user.nickname || "",
    });
    const [errors, setErrors] = useState<Errors>({ nickname: "" });
    const queryClient = useQueryClient();
    const { logoutUser } = useLogout();
    const [editingNickname, setEditingNickname] = useState<boolean>(false);

    const { data: notificationSettings } = useQuery<NotificationSetting[]>({
        queryKey: ["userNotificationSettings", user.id],
        queryFn: () => findUserNotificationSetting(user.id),
        enabled: !!user.id,
    });

    const settings = useMemo(() => notificationSettings ?? [], [notificationSettings]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            emailAddress: user.emailAddress || "",
            nickname: user.nickname || "",
        }));

        if (settings && Array.isArray(settings)) {
            const getNotificationValue = (type: string): boolean => {
                const setting = settings.find((s) => s.message_type === type);
                return setting
                    ? setting.email_frequency !== null &&
                          setting.email_frequency !== "" &&
                          setting.email_frequency !== "never"
                    : false;
            };

            setFormData((prev) => ({
                ...prev,
                notifications: {
                    statusChanges: getNotificationValue(NOTIFICATION_TYPES.STATUS_CHANGES),
                    commentsOnMyListings: getNotificationValue(NOTIFICATION_TYPES.COMMENTS_ON_MY_LISTINGS),
                    newListings: getNotificationValue(NOTIFICATION_TYPES.NEW_LISTINGS),
                },
            }));
        }
    }, [user, settings]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (name === "nickname") {
            setErrors((prev) => ({ ...prev, nickname: "" }));
        }
    };

    const validateNickname = (nickname: string): string => {
        if (!nickname.trim()) return "Nickname is required";
        if (nickname.length < 2) return "Nickname must be at least 2 characters long";
        if (nickname.length > 30) return "Nickname must be less than 30 characters";
        return "";
    };

    const handleToggleChange = async (setting: keyof FormData["notifications"]) => {
        const updatedValue = !formData.notifications[setting];
        setFormData((prev) => ({
            ...prev,
            notifications: {
                ...prev.notifications,
                [setting]: updatedValue,
            },
        }));

        try {
            const newSettings = [...settings];
            const updatedSettings = newSettings.map((s) => {
                if (
                    (setting === "statusChanges" && s.message_type === NOTIFICATION_TYPES.STATUS_CHANGES) ||
                    (setting === "commentsOnMyListings" &&
                        s.message_type === NOTIFICATION_TYPES.COMMENTS_ON_MY_LISTINGS) ||
                    (setting === "newListings" && s.message_type === NOTIFICATION_TYPES.NEW_LISTINGS)
                ) {
                    return {
                        ...s,
                        email_frequency: updatedValue ? "daily" : "never",
                    };
                }
                return s;
            });

            await syncUser({
                id: user.id,
                nickname: formData.nickname,
                email: formData.emailAddress,
                notification_settings: updatedSettings,
            });

            queryClient.invalidateQueries({ queryKey: ["userNotificationSettings", user.id] });
            toast.success("Notification preference updated");
        } catch (err) {
            toast.error("Failed to update setting");
        }
    };

    return (
        <div className="tw-flex tw-flex-col tw-flex-1">
            <div className="tw-flex tw-flex-col tw-flex-1 tw-justify-between">
                <div className="tw-p-6 tw-space-y-6">
                    {/* Email Field */}
                    <div className="tw-space-y-2">
                        <label className="tw-text-[14px] tw-font-noto tw-text-[#666666]">Email</label>
                        <input
                            type="email"
                            name="email_address"
                            value={formData.emailAddress}
                            disabled
                            className="tw-w-full tw-p-3 tw-border tw-border-[#9E9E9E] tw-rounded-lg tw-text-[14px]"
                        />
                    </div>

                    {/* Nickname Field */}
                    <div className="tw-space-y-2">
                        <label className="tw-text-[14px] tw-font-noto tw-text-[#666666]">Nickname</label>
                        <div className="tw-relative">
                            <input
                                type="text"
                                name="nickname"
                                value={formData.nickname}
                                disabled={!editingNickname}
                                onChange={handleInputChange}
                                className={clsx(
                                    "tw-w-full tw-p-3 tw-border tw-rounded-lg tw-text-[14px]",
                                    errors.nickname ? "tw-border-red-500" : "tw-border-[#D9D9D9]",
                                    editingNickname ? "tw-bg-white" : "tw-bg-[#F3F3F3]",
                                )}
                                placeholder="Enter your nickname"
                            />
                            <button
                                type="button"
                                onClick={async () => {
                                    if (editingNickname) {
                                        const error = validateNickname(formData.nickname);
                                        if (error) {
                                            setErrors({ nickname: error });
                                            return;
                                        }

                                        try {
                                            toast.loading("Updating nickname...", { id: "nickname" });
                                            await syncUser({
                                                id: user.id,
                                                nickname: formData.nickname,
                                                email: formData.emailAddress,
                                                notification_settings: settings,
                                            });
                                            queryClient.invalidateQueries({ queryKey: ["user", user.id] });
                                            toast.success("Nickname updated!", { id: "nickname" });
                                            setEditingNickname(false);
                                        } catch (err) {
                                            toast.error("Failed to update nickname", { id: "nickname" });
                                        }
                                    } else {
                                        setEditingNickname(true);
                                    }
                                }}
                                className="tw-absolute tw-top-1/2 tw-right-3 -tw-translate-y-1/2 tw-bg-transparent tw-text-black"
                            >
                                {editingNickname ? (
                                    <FaCheck className="tw-h-5 tw-w-5" />
                                ) : (
                                    <FaEdit className="tw-w-5 tw-h-5" />
                                )}
                            </button>
                        </div>
                        {errors.nickname && (
                            <p className="tw-text-red-500 tw-text-sm tw-font-noto">{errors.nickname}</p>
                        )}
                    </div>

                    <hr />

                    {/* Email Notifications Section */}
                    <div className="tw-space-y-4">
                        <h2 className="tw-text-[18px] tw-font-merriweather tw-font-bold tw-text-black">
                            Email Notifications
                        </h2>
                        <div className="tw-space-y-4">
                            {[
                                {
                                    label: "Status changes on listing you commented on",
                                    setting: "statusChanges" as const,
                                },
                                {
                                    label: "Comments by other users on listings you commented on",
                                    setting: "commentsOnMyListings" as const,
                                },
                                {
                                    label: "New listings for me",
                                    setting: "newListings" as const,
                                },
                            ].map(({ label, setting }) => (
                                <div key={setting} className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-[14px] tw-font-noto tw-text-[#666666]">{label}</span>
                                    <div style={{ "--switch-active-color": "#10A1D9" } as React.CSSProperties}>
                                        <Switch
                                            checked={formData.notifications[setting]}
                                            onChange={() => handleToggleChange(setting)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <button onClick={logoutUser} className="tw-bg-gray-200 tw-text-red-500 tw-flex tw-rounded-lg">
                        Logout
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserSettings;
